import type {
  StorefrontContentBenefitItemConfig,
  StorefrontContentCtaConfig,
  StorefrontContentDestinationTarget,
  StorefrontContentFaqItemConfig,
  StorefrontContentIconToken,
  StorefrontContentLayoutToken,
  StorefrontContentLocale,
  StorefrontContentLocalizedText,
  StorefrontContentLocalizedRichText,
  StorefrontContentMediaConfig,
  StorefrontContentRichTextBlockNode,
  StorefrontContentRichTextDocument,
  StorefrontContentRichTextInlineNode,
  StorefrontContentRichTextListItemNode,
  StorefrontContentRichTextLinkNode,
  StorefrontContentRichTextMark,
  StorefrontContentRichTextVersion,
  StorefrontContentSectionConfig,
  StorefrontContentSectionKind,
  StorefrontContentSectionTone,
  StorefrontContentTestimonialsItemConfig,
  StorefrontNewsletterCollectionConfig,
  StorefrontNewsletterConsentVersion,
  StorefrontNewsletterContactType,
  StorefrontPageConfig,
} from '../../types/storefront-engine';
import { isStorefrontFooterQuickLinkTarget } from './navigation';
import { sanitizeStorefrontContentMediaConfig } from './public-media-reference';

type UnknownRecord = Record<string, unknown>;

export const STOREFRONT_CONTENT_LOCALES = ['ar', 'he', 'en'] as const;
export const STOREFRONT_CONTENT_SECTION_KINDS = [
  'benefits',
  'faq',
  'aboutStore',
  'testimonials',
  'newsletterCta',
  'editorialBanner',
] as const satisfies readonly StorefrontContentSectionKind[];
export const STOREFRONT_CONTENT_ICON_TOKENS = [
  'badge-check',
  'heart-handshake',
  'leaf',
  'package-check',
  'shield-check',
  'sparkles',
  'truck',
] as const satisfies readonly StorefrontContentIconToken[];
export const STOREFRONT_CONTENT_LAYOUT_TOKENS = [
  'cards',
  'list',
  'accordion',
  'story',
  'quotes',
  'banner',
  'split',
  'overlay',
] as const satisfies readonly StorefrontContentLayoutToken[];
export const STOREFRONT_CONTENT_TONES = [
  'neutral',
  'warm',
  'accent',
] as const satisfies readonly StorefrontContentSectionTone[];
export const STOREFRONT_NEWSLETTER_CONTACT_TYPES = [
  'email',
  'phone',
] as const satisfies readonly StorefrontNewsletterContactType[];
export const STOREFRONT_NEWSLETTER_CONSENT_VERSION =
  'newsletter-consent-v1' as const satisfies StorefrontNewsletterConsentVersion;
export const DEFAULT_STOREFRONT_NEWSLETTER_COLLECTION_CONFIG = {
  enabled: false,
  contactMethods: ['email', 'phone'],
  consentVersion: STOREFRONT_NEWSLETTER_CONSENT_VERSION,
} as const satisfies StorefrontNewsletterCollectionConfig;

export const STOREFRONT_CONTENT_BENEFIT_ITEM_MIN = 2;
export const STOREFRONT_CONTENT_BENEFIT_ITEM_LIMIT = 6;
export const STOREFRONT_CONTENT_FAQ_ITEM_MIN = 2;
export const STOREFRONT_CONTENT_FAQ_ITEM_LIMIT = 8;
export const STOREFRONT_CONTENT_ABOUT_PARAGRAPH_MIN = 1;
export const STOREFRONT_CONTENT_ABOUT_PARAGRAPH_LIMIT = 3;
export const STOREFRONT_CONTENT_TESTIMONIAL_ITEM_MIN = 1;
export const STOREFRONT_CONTENT_TESTIMONIAL_ITEM_LIMIT = 6;
export const STOREFRONT_CONTENT_SHORT_TEXT_MAX_LENGTH = 120;
export const STOREFRONT_CONTENT_BODY_TEXT_MAX_LENGTH = 900;
export const STOREFRONT_CONTENT_RICH_TEXT_VERSION = 1;
export const STOREFRONT_CONTENT_RICH_TEXT_LINK_VERSION = 2;
export const STOREFRONT_CONTENT_RICH_TEXT_MARKS = [
  'bold',
  'italic',
] as const satisfies readonly StorefrontContentRichTextMark[];
export const STOREFRONT_CONTENT_RICH_TEXT_BLOCK_LIMIT = 12;
export const STOREFRONT_CONTENT_RICH_TEXT_LIST_ITEM_LIMIT = 12;
export const STOREFRONT_CONTENT_RICH_TEXT_INLINE_NODE_LIMIT = 80;
export const STOREFRONT_CONTENT_RICH_TEXT_LINK_TEXT_MAX_LENGTH = 160;
export const STOREFRONT_CONTENT_RICH_TEXT_TEXT_NODE_MAX_LENGTH =
  STOREFRONT_CONTENT_BODY_TEXT_MAX_LENGTH;

const ID_RE = /^[a-z0-9][a-z0-9_-]{1,39}$/;
const RAW_HTML_URL_OR_MARKDOWN_RE =
  /<|>|<\/|(?:^|[\s"'([{])(?:(?:https?|ftp):\/\/|\/\/[^\s]+|(?:mailto|tel|sms):)|www\.|(?:^|[\s"'([{])(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,24}|xn--[a-z0-9-]{2,59})(?=$|[/?#:\s"'’”)\]}.;,!؟،])|javascript:|data:|vbscript:|on[a-z]+\s*=|style\s*=|iframe|script|<style|<\/style|!\[[^\]]*\]|(?:^|\s)\*\*[^*]+\*\*(?:\s|$)|(?:^|\s)__[^_]+__(?:\s|$)|(?:^|\s)[*_][^*_]+[*_](?:\s|$)|(?:^|\n)\s{0,3}#{1,6}\s|(?:^|\n)\s{0,3}[-*+]\s|\[[^\]]+\]\([^)]+\)/iu;

type RichTextSanitizeState = {
  textLength: number;
  inlineNodeCount: number;
};

type RichTextSanitizeOptions = {
  readonly required: boolean;
  readonly maxLength: number;
};

type RichTextLinkRange = {
  readonly start: number;
  readonly end: number;
  readonly target: StorefrontContentDestinationTarget;
};

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  return Object.keys(value).every((key) => keys.includes(key));
}

function isOneOf<TValue extends string>(
  value: unknown,
  values: readonly TValue[],
): value is TValue {
  return typeof value === 'string' && values.includes(value as TValue);
}

function isSafeId(value: unknown): value is string {
  return typeof value === 'string' && ID_RE.test(value);
}

function normalizePlainText(value: string): string | null {
  const normalized = value.replace(/\r\n?/g, '\n').trim();
  if (normalized.length === 0) return '';
  if (RAW_HTML_URL_OR_MARKDOWN_RE.test(normalized)) return null;
  for (const char of normalized) {
    const codePoint = char.codePointAt(0) ?? 0;
    if (
      (codePoint <= 0x1f && codePoint !== 0x09 && codePoint !== 0x0a) ||
      codePoint === 0x7f ||
      (codePoint >= 0x202a && codePoint <= 0x202e) ||
      (codePoint >= 0x2066 && codePoint <= 0x2069)
    ) {
      return null;
    }
  }
  return normalized;
}

function normalizeRichTextText(value: string): string | null {
  const normalized = value.replace(/\r\n?/g, '\n');
  if (RAW_HTML_URL_OR_MARKDOWN_RE.test(normalized)) return null;
  for (const char of normalized) {
    const codePoint = char.codePointAt(0) ?? 0;
    if (
      (codePoint <= 0x1f && codePoint !== 0x09 && codePoint !== 0x0a) ||
      codePoint === 0x7f ||
      (codePoint >= 0x202a && codePoint <= 0x202e) ||
      (codePoint >= 0x2066 && codePoint <= 0x2069)
    ) {
      return null;
    }
  }
  return normalized;
}

function normalizeRichTextMarks(
  value: unknown,
): readonly StorefrontContentRichTextMark[] | undefined | null {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length > STOREFRONT_CONTENT_RICH_TEXT_MARKS.length) {
    return null;
  }
  const marks: StorefrontContentRichTextMark[] = [];
  for (const mark of value) {
    if (!isOneOf(mark, STOREFRONT_CONTENT_RICH_TEXT_MARKS) || marks.includes(mark)) return null;
    marks.push(mark);
  }
  const sorted = STOREFRONT_CONTENT_RICH_TEXT_MARKS.filter((mark) => marks.includes(mark));
  return sorted.length > 0 ? sorted : undefined;
}

function addRichTextInlineNode(
  state: RichTextSanitizeState,
  node: StorefrontContentRichTextInlineNode,
): boolean {
  state.inlineNodeCount += 1;
  return state.inlineNodeCount <= STOREFRONT_CONTENT_RICH_TEXT_INLINE_NODE_LIMIT && Boolean(node);
}

function sanitizeRichTextTextNode(
  value: UnknownRecord,
  state: RichTextSanitizeState,
  maxLength: number,
): readonly StorefrontContentRichTextInlineNode[] | null {
  if (!hasOnlyKeys(value, ['type', 'text', 'marks']) || typeof value.text !== 'string') {
    return null;
  }
  const marks = normalizeRichTextMarks(value.marks);
  if (marks === null) return null;
  const normalized = normalizeRichTextText(value.text);
  if (
    normalized === null ||
    normalized.length > STOREFRONT_CONTENT_RICH_TEXT_TEXT_NODE_MAX_LENGTH
  ) {
    return null;
  }
  state.textLength += normalized.length;
  if (state.textLength > maxLength) return null;

  const nodes: StorefrontContentRichTextInlineNode[] = [];
  const segments = normalized.split('\n');
  segments.forEach((segment, index) => {
    if (segment.length > 0) {
      const node =
        marks === undefined
          ? ({ type: 'text', text: segment } satisfies StorefrontContentRichTextInlineNode)
          : ({ type: 'text', text: segment, marks } satisfies StorefrontContentRichTextInlineNode);
      if (addRichTextInlineNode(state, node)) nodes.push(node);
    }
    if (index < segments.length - 1) {
      const softBreak = { type: 'softBreak' } satisfies StorefrontContentRichTextInlineNode;
      if (addRichTextInlineNode(state, softBreak)) nodes.push(softBreak);
    }
  });
  return state.inlineNodeCount <= STOREFRONT_CONTENT_RICH_TEXT_INLINE_NODE_LIMIT ? nodes : null;
}

function sanitizeRichTextLinkNode(
  value: UnknownRecord,
  state: RichTextSanitizeState,
  maxLength: number,
): readonly StorefrontContentRichTextInlineNode[] | null {
  if (!hasOnlyKeys(value, ['type', 'text', 'target', 'marks']) || typeof value.text !== 'string') {
    return null;
  }
  const marks = normalizeRichTextMarks(value.marks);
  const target = sanitizeDestination(value.target);
  const normalized = normalizeRichTextText(value.text);
  if (
    marks === null ||
    !target ||
    normalized === null ||
    normalized.includes('\n') ||
    normalized.trim().length === 0 ||
    normalized.length > STOREFRONT_CONTENT_RICH_TEXT_LINK_TEXT_MAX_LENGTH
  ) {
    return null;
  }
  state.textLength += normalized.length;
  if (state.textLength > maxLength) return null;
  const node =
    marks === undefined
      ? ({
          type: 'link',
          text: normalized,
          target,
        } satisfies StorefrontContentRichTextLinkNode)
      : ({
          type: 'link',
          text: normalized,
          target,
          marks,
        } satisfies StorefrontContentRichTextLinkNode);
  return addRichTextInlineNode(state, node) ? [node] : null;
}

function sanitizeRichTextInlineNode(
  value: unknown,
  state: RichTextSanitizeState,
  maxLength: number,
  version: number,
): readonly StorefrontContentRichTextInlineNode[] | null {
  if (!isRecord(value) || typeof value.type !== 'string') return null;
  if (value.type === 'softBreak') {
    if (!hasOnlyKeys(value, ['type'])) return null;
    const node = { type: 'softBreak' } satisfies StorefrontContentRichTextInlineNode;
    return addRichTextInlineNode(state, node) ? [node] : null;
  }
  if (value.type === 'text') return sanitizeRichTextTextNode(value, state, maxLength);
  if (value.type === 'link' && version === STOREFRONT_CONTENT_RICH_TEXT_LINK_VERSION) {
    return sanitizeRichTextLinkNode(value, state, maxLength);
  }
  return null;
}

function richTextInlinePlainText(nodes: readonly StorefrontContentRichTextInlineNode[]): string {
  return nodes
    .map((node) => {
      if (node.type === 'softBreak') return '\n';
      return node.text;
    })
    .join('');
}

function sanitizeRichTextInlineNodes(
  value: unknown,
  state: RichTextSanitizeState,
  maxLength: number,
  version: number,
): readonly StorefrontContentRichTextInlineNode[] | null {
  if (!Array.isArray(value) || value.length > STOREFRONT_CONTENT_RICH_TEXT_INLINE_NODE_LIMIT) {
    return null;
  }
  const nodes: StorefrontContentRichTextInlineNode[] = [];
  for (const node of value) {
    const sanitized = sanitizeRichTextInlineNode(node, state, maxLength, version);
    if (!sanitized) return null;
    nodes.push(...sanitized);
  }
  return nodes;
}

function sanitizeRichTextParagraphNode(
  value: UnknownRecord,
  state: RichTextSanitizeState,
  maxLength: number,
  version: number,
): StorefrontContentRichTextBlockNode | null {
  if (!hasOnlyKeys(value, ['type', 'children'])) return null;
  const children = sanitizeRichTextInlineNodes(value.children, state, maxLength, version);
  if (!children || richTextInlinePlainText(children).trim().length === 0) return null;
  return { type: 'paragraph', children };
}

function sanitizeRichTextListItemNode(
  value: unknown,
  state: RichTextSanitizeState,
  maxLength: number,
  version: number,
): StorefrontContentRichTextListItemNode | null {
  if (!isRecord(value) || value.type !== 'listItem' || !hasOnlyKeys(value, ['type', 'children'])) {
    return null;
  }
  const children = sanitizeRichTextInlineNodes(value.children, state, maxLength, version);
  if (!children || richTextInlinePlainText(children).trim().length === 0) return null;
  return { type: 'listItem', children };
}

function sanitizeRichTextListNode(
  value: UnknownRecord,
  state: RichTextSanitizeState,
  maxLength: number,
  version: number,
): StorefrontContentRichTextBlockNode | null {
  if (
    !hasOnlyKeys(value, ['type', 'style', 'items']) ||
    !isOneOf(value.style, ['unordered', 'ordered']) ||
    !Array.isArray(value.items) ||
    value.items.length < 1 ||
    value.items.length > STOREFRONT_CONTENT_RICH_TEXT_LIST_ITEM_LIMIT
  ) {
    return null;
  }
  const items = value.items.map((item) =>
    sanitizeRichTextListItemNode(item, state, maxLength, version),
  );
  return items.some((item) => item === null)
    ? null
    : { type: 'list', style: value.style, items: items as StorefrontContentRichTextListItemNode[] };
}

function sanitizeRichTextBlockNode(
  value: unknown,
  state: RichTextSanitizeState,
  maxLength: number,
  version: number,
): StorefrontContentRichTextBlockNode | null {
  if (!isRecord(value) || typeof value.type !== 'string') return null;
  if (value.type === 'paragraph') {
    return sanitizeRichTextParagraphNode(value, state, maxLength, version);
  }
  if (value.type === 'list') return sanitizeRichTextListNode(value, state, maxLength, version);
  return null;
}

export function sanitizeStorefrontContentRichTextDocument(
  value: unknown,
  options: RichTextSanitizeOptions,
): StorefrontContentRichTextDocument | null {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ['type', 'version', 'children']) ||
    value.type !== 'doc' ||
    (value.version !== STOREFRONT_CONTENT_RICH_TEXT_VERSION &&
      value.version !== STOREFRONT_CONTENT_RICH_TEXT_LINK_VERSION) ||
    !Array.isArray(value.children) ||
    value.children.length > STOREFRONT_CONTENT_RICH_TEXT_BLOCK_LIMIT ||
    (options.required && value.children.length < 1)
  ) {
    return null;
  }
  if (value.children.length === 0) {
    return options.required ? null : { type: 'doc', version: value.version, children: [] };
  }
  const version = value.version as StorefrontContentRichTextVersion;
  const state: RichTextSanitizeState = { textLength: 0, inlineNodeCount: 0 };
  const children = value.children.map((node) =>
    sanitizeRichTextBlockNode(node, state, options.maxLength, version),
  );
  if (children.some((node) => node === null)) return null;
  const document = {
    type: 'doc',
    version,
    children: children as StorefrontContentRichTextBlockNode[],
  } satisfies StorefrontContentRichTextDocument;
  if (options.required && extractStorefrontContentRichTextPlainText(document).trim().length === 0) {
    return null;
  }
  if (
    !options.required &&
    extractStorefrontContentRichTextPlainText(document).trim().length === 0
  ) {
    return { type: 'doc', version: value.version, children: [] };
  }
  return document;
}

export function sanitizeStorefrontContentLocalizedRichText(
  value: unknown,
  options: RichTextSanitizeOptions,
): StorefrontContentLocalizedRichText | null {
  if (!isRecord(value) || !hasOnlyKeys(value, STOREFRONT_CONTENT_LOCALES)) return null;
  const ar = sanitizeStorefrontContentRichTextDocument(value.ar, options);
  const he = sanitizeStorefrontContentRichTextDocument(value.he, options);
  const en = sanitizeStorefrontContentRichTextDocument(value.en, options);
  return ar && he && en ? { ar, he, en } : null;
}

function sanitizeOptionalLocalizedRichText(
  value: unknown,
  maxLength: number,
  required = false,
): StorefrontContentLocalizedRichText | undefined | null {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return sanitizeStorefrontContentLocalizedRichText(value, { required, maxLength });
}

export function extractStorefrontContentRichTextPlainText(
  document: StorefrontContentRichTextDocument,
): string {
  return document.children
    .map((block) => {
      if (block.type === 'paragraph') return richTextInlinePlainText(block.children);
      return block.items.map((item) => richTextInlinePlainText(item.children)).join('\n');
    })
    .join('\n\n')
    .trim();
}

function plainTextToRichTextInlineNodes(
  value: string,
  marks?: readonly StorefrontContentRichTextMark[],
  linkRange?: RichTextLinkRange,
  baseOffset = 0,
): readonly StorefrontContentRichTextInlineNode[] {
  const children: StorefrontContentRichTextInlineNode[] = [];
  const segments = value.split('\n');
  let offset = baseOffset;
  segments.forEach((segment, index) => {
    if (segment.length > 0) {
      children.push(...plainTextSegmentToRichTextInlineNodes(segment, offset, marks, linkRange));
    }
    offset += segment.length;
    if (index < segments.length - 1) {
      children.push({ type: 'softBreak' });
      offset += 1;
    }
  });
  return children;
}

function plainTextSegmentToRichTextInlineNodes(
  segment: string,
  baseOffset: number,
  marks?: readonly StorefrontContentRichTextMark[],
  linkRange?: RichTextLinkRange,
): readonly StorefrontContentRichTextInlineNode[] {
  if (!linkRange) {
    return marks?.length
      ? [{ type: 'text', text: segment, marks }]
      : [{ type: 'text', text: segment }];
  }
  const segmentStart = baseOffset;
  const segmentEnd = baseOffset + segment.length;
  const linkStart = Math.max(linkRange.start, segmentStart);
  const linkEnd = Math.min(linkRange.end, segmentEnd);
  if (linkStart >= linkEnd) {
    return marks?.length
      ? [{ type: 'text', text: segment, marks }]
      : [{ type: 'text', text: segment }];
  }

  const nodes: StorefrontContentRichTextInlineNode[] = [];
  const before = segment.slice(0, linkStart - segmentStart);
  const linked = segment.slice(linkStart - segmentStart, linkEnd - segmentStart);
  const after = segment.slice(linkEnd - segmentStart);
  if (before) {
    nodes.push(
      marks?.length ? { type: 'text', text: before, marks } : { type: 'text', text: before },
    );
  }
  nodes.push(
    marks?.length
      ? { type: 'link', text: linked, target: linkRange.target, marks }
      : { type: 'link', text: linked, target: linkRange.target },
  );
  if (after) {
    nodes.push(
      marks?.length ? { type: 'text', text: after, marks } : { type: 'text', text: after },
    );
  }
  return nodes;
}

export function createStorefrontContentRichTextDocumentFromPlainText(
  value: string,
  options: {
    readonly marks?: readonly StorefrontContentRichTextMark[];
    readonly listStyle?: 'unordered' | 'ordered';
    readonly maxLength?: number;
  } = {},
): StorefrontContentRichTextDocument | null {
  const normalized = normalizeRichTextText(value);
  if (normalized === null) return null;
  const marks = normalizeRichTextMarks(options.marks);
  if (marks === null) return null;
  const text = normalized.trim();
  if (!text) return { type: 'doc', version: 1, children: [] };
  const children: StorefrontContentRichTextBlockNode[] = [];
  if (options.listStyle) {
    const items = text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map(
        (line) =>
          ({
            type: 'listItem',
            children: plainTextToRichTextInlineNodes(line, marks),
          }) satisfies StorefrontContentRichTextListItemNode,
      );
    children.push({ type: 'list', style: options.listStyle, items });
  } else {
    for (const paragraph of text.split(/\n{2,}/)) {
      const paragraphText = paragraph.trim();
      if (!paragraphText) continue;
      children.push({
        type: 'paragraph',
        children: plainTextToRichTextInlineNodes(paragraphText, marks),
      });
    }
  }
  return sanitizeStorefrontContentRichTextDocument(
    { type: 'doc', version: 1, children },
    { required: false, maxLength: options.maxLength ?? STOREFRONT_CONTENT_BODY_TEXT_MAX_LENGTH },
  );
}

export function createStorefrontContentRichTextDocumentWithInlineLink(
  value: string,
  options: {
    readonly selectionStart: number;
    readonly selectionEnd: number;
    readonly target: StorefrontContentDestinationTarget;
    readonly marks?: readonly StorefrontContentRichTextMark[];
    readonly listStyle?: 'unordered' | 'ordered';
    readonly maxLength?: number;
  },
): StorefrontContentRichTextDocument | null {
  const normalized = normalizeRichTextText(value);
  if (normalized === null) return null;
  const target = sanitizeDestination(options.target);
  const marks = normalizeRichTextMarks(options.marks);
  if (!target || marks === null) return null;

  const leadingTrimLength = normalized.length - normalized.trimStart().length;
  const text = normalized.trim();
  if (!text) return null;
  const selectionStart = Math.max(
    0,
    Math.min(text.length, options.selectionStart - leadingTrimLength),
  );
  const selectionEnd = Math.max(0, Math.min(text.length, options.selectionEnd - leadingTrimLength));
  if (
    selectionEnd <= selectionStart ||
    text.slice(selectionStart, selectionEnd).trim().length === 0
  ) {
    return null;
  }

  const linkRange = {
    start: selectionStart,
    end: selectionEnd,
    target,
  } satisfies RichTextLinkRange;
  const children: StorefrontContentRichTextBlockNode[] = [];
  if (options.listStyle) {
    let cursor = 0;
    for (const line of text.split('\n')) {
      const lineStart = cursor;
      cursor += line.length + 1;
      const lineText = line.trim();
      if (!lineText) continue;
      const lineLeading = line.length - line.trimStart().length;
      children.push({
        type: 'list',
        style: options.listStyle,
        items: [
          {
            type: 'listItem',
            children: plainTextToRichTextInlineNodes(
              lineText,
              marks,
              linkRange,
              lineStart + lineLeading,
            ),
          },
        ],
      });
    }
    const first = children[0];
    if (!first || first.type !== 'list') return null;
    const items = children.flatMap((child) => (child.type === 'list' ? child.items : []));
    return sanitizeStorefrontContentRichTextDocument(
      {
        type: 'doc',
        version: STOREFRONT_CONTENT_RICH_TEXT_LINK_VERSION,
        children: [{ type: 'list', style: options.listStyle, items }],
      },
      { required: false, maxLength: options.maxLength ?? STOREFRONT_CONTENT_BODY_TEXT_MAX_LENGTH },
    );
  }

  let cursor = 0;
  for (const paragraph of text.split(/\n{2,}/)) {
    const paragraphStart = text.indexOf(paragraph, cursor);
    cursor = paragraphStart + paragraph.length;
    const paragraphText = paragraph.trim();
    if (!paragraphText) continue;
    const paragraphLeading = paragraph.length - paragraph.trimStart().length;
    children.push({
      type: 'paragraph',
      children: plainTextToRichTextInlineNodes(
        paragraphText,
        marks,
        linkRange,
        paragraphStart + paragraphLeading,
      ),
    });
  }
  return sanitizeStorefrontContentRichTextDocument(
    { type: 'doc', version: STOREFRONT_CONTENT_RICH_TEXT_LINK_VERSION, children },
    { required: false, maxLength: options.maxLength ?? STOREFRONT_CONTENT_BODY_TEXT_MAX_LENGTH },
  );
}

export function hasStorefrontContentRichTextLinks(
  document: StorefrontContentRichTextDocument | null | undefined,
): boolean {
  if (!document) return false;
  return document.children.some((block) => {
    if (block.type === 'paragraph') return block.children.some((node) => node.type === 'link');
    return block.items.some((item) => item.children.some((node) => node.type === 'link'));
  });
}

function unlinkRichTextInlineNode(
  node: StorefrontContentRichTextInlineNode,
): StorefrontContentRichTextInlineNode {
  if (node.type !== 'link') return node;
  return node.marks?.length
    ? { type: 'text', text: node.text, marks: node.marks }
    : { type: 'text', text: node.text };
}

function transformRichTextLinks(
  document: StorefrontContentRichTextDocument,
  shouldUnlink: (start: number, end: number) => boolean,
): StorefrontContentRichTextDocument {
  let cursor = 0;
  let hasLinks = false;
  const transformInlineNodes = (
    nodes: readonly StorefrontContentRichTextInlineNode[],
  ): StorefrontContentRichTextInlineNode[] =>
    nodes.map((node) => {
      const start = cursor;
      const end = cursor + (node.type === 'softBreak' ? 1 : node.text.length);
      cursor = end;
      if (node.type === 'link' && shouldUnlink(start, end)) return unlinkRichTextInlineNode(node);
      if (node.type === 'link') hasLinks = true;
      return node;
    });

  const children = document.children.map((block, blockIndex) => {
    if (blockIndex > 0) cursor += 2;
    if (block.type === 'paragraph') {
      return {
        ...block,
        children: transformInlineNodes(block.children),
      } satisfies StorefrontContentRichTextBlockNode;
    }
    return {
      ...block,
      items: block.items.map((item, itemIndex) => {
        if (itemIndex > 0) cursor += 1;
        return {
          ...item,
          children: transformInlineNodes(item.children),
        };
      }),
    } satisfies StorefrontContentRichTextBlockNode;
  });
  return {
    type: 'doc',
    version: hasLinks
      ? STOREFRONT_CONTENT_RICH_TEXT_LINK_VERSION
      : STOREFRONT_CONTENT_RICH_TEXT_VERSION,
    children,
  };
}

export function removeStorefrontContentRichTextLinks(
  document: StorefrontContentRichTextDocument,
): StorefrontContentRichTextDocument {
  return transformRichTextLinks(document, () => true);
}

export function removeStorefrontContentRichTextLinksInRange(
  document: StorefrontContentRichTextDocument,
  selectionStart: number,
  selectionEnd: number,
): StorefrontContentRichTextDocument {
  if (selectionEnd <= selectionStart) return removeStorefrontContentRichTextLinks(document);
  return transformRichTextLinks(
    document,
    (start, end) => end > selectionStart && start < selectionEnd,
  );
}

function richTextTargetRequiresRemoteReadiness(
  target: StorefrontContentDestinationTarget,
): boolean {
  return target.type === 'store-category' || target.type === 'contact';
}

function richTextDocumentRequiresRemoteReadiness(
  document: StorefrontContentRichTextDocument | null | undefined,
): boolean {
  if (!document) return false;
  return document.children.some((block) => {
    if (block.type === 'paragraph') {
      return block.children.some(
        (node) => node.type === 'link' && richTextTargetRequiresRemoteReadiness(node.target),
      );
    }
    return block.items.some((item) =>
      item.children.some(
        (node) => node.type === 'link' && richTextTargetRequiresRemoteReadiness(node.target),
      ),
    );
  });
}

function localizedRichTextRequiresRemoteReadiness(
  value: StorefrontContentLocalizedRichText | null | undefined,
): boolean {
  if (!value) return false;
  return STOREFRONT_CONTENT_LOCALES.some((locale) =>
    richTextDocumentRequiresRemoteReadiness(value[locale]),
  );
}

export function storefrontContentSectionRequiresRemoteReadiness(
  config: StorefrontContentSectionConfig | null | undefined,
): boolean {
  if (!config) return false;
  if ('bodyRichText' in config && localizedRichTextRequiresRemoteReadiness(config.bodyRichText)) {
    return true;
  }
  if (config.kind === 'benefits') {
    return config.items.some((item) => localizedRichTextRequiresRemoteReadiness(item.bodyRichText));
  }
  if (config.kind === 'faq') {
    return config.items.some((item) =>
      localizedRichTextRequiresRemoteReadiness(item.answerRichText),
    );
  }
  if (config.kind === 'aboutStore') {
    return (
      config.paragraphsRichText?.some((paragraph) =>
        localizedRichTextRequiresRemoteReadiness(paragraph),
      ) ?? false
    );
  }
  if (config.kind === 'testimonials') {
    return config.items.some((item) =>
      localizedRichTextRequiresRemoteReadiness(item.quoteRichText),
    );
  }
  if (config.kind === 'newsletterCta') {
    return Boolean(config.button && richTextTargetRequiresRemoteReadiness(config.button.target));
  }
  if (config.kind === 'editorialBanner') {
    return (
      Boolean(config.button && richTextTargetRequiresRemoteReadiness(config.button.target)) ||
      config.media.desktop !== null ||
      config.media.mobile !== null
    );
  }
  return false;
}

export function storefrontContentConfigRequiresRemoteReadiness(
  config: StorefrontPageConfig | null | undefined,
): boolean {
  if (!config) return false;
  return config.sections.some((section) => {
    if (section.status !== 'enabled' && section.status !== 'hidden') {
      return false;
    }
    if ('lifecycle' in section && section.lifecycle?.state === 'draft-deleted') return false;
    if (!('config' in section)) return false;
    const contentSection = section.config?.content?.contentSection;
    return storefrontContentSectionRequiresRemoteReadiness(contentSection);
  });
}

function sanitizeLocalizedText(
  value: unknown,
  options: {
    readonly required: boolean;
    readonly maxLength: number;
  },
): StorefrontContentLocalizedText | null {
  if (!isRecord(value) || !hasOnlyKeys(value, STOREFRONT_CONTENT_LOCALES)) return null;
  const ar = typeof value.ar === 'string' ? normalizePlainText(value.ar) : null;
  const he = typeof value.he === 'string' ? normalizePlainText(value.he) : null;
  const en = typeof value.en === 'string' ? normalizePlainText(value.en) : null;
  if (ar === null || he === null || en === null) return null;
  if (
    ar.length > options.maxLength ||
    he.length > options.maxLength ||
    en.length > options.maxLength
  ) {
    return null;
  }
  if (options.required && (!ar || !he || !en)) return null;
  return { ar, he, en };
}

/**
 * F-140: the exact rules `content.*` headings use (exact ar/he/en keys, plain-text
 * normalization, trim, max length, non-empty when required), exported so
 * `categories.rail`'s optional per-instance heading reuses them verbatim.
 */
export function sanitizeStorefrontContentLocalizedText(
  value: unknown,
  options: {
    readonly required: boolean;
    readonly maxLength: number;
  },
): StorefrontContentLocalizedText | null {
  return sanitizeLocalizedText(value, options);
}

function sanitizeOptionalLocalizedText(
  value: unknown,
  maxLength: number,
): StorefrontContentLocalizedText | null {
  if (value === null || value === undefined) return { ar: '', he: '', en: '' };
  const localized = sanitizeLocalizedText(value, { required: false, maxLength });
  if (!localized) return null;
  return localized.ar || localized.he || localized.en ? localized : { ar: '', he: '', en: '' };
}

function sanitizeDestination(value: unknown): StorefrontContentDestinationTarget | null {
  return isStorefrontFooterQuickLinkTarget(value) ? structuredClone(value) : null;
}

function hasUniqueItemIds(items: readonly { readonly id: string }[]): boolean {
  const ids = new Set<string>();
  for (const item of items) {
    if (ids.has(item.id)) return false;
    ids.add(item.id);
  }
  return true;
}

function sanitizeBenefitItem(value: unknown): StorefrontContentBenefitItemConfig | null {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ['id', 'title', 'body', 'bodyRichText', 'icon']) ||
    !('id' in value) ||
    !('title' in value)
  ) {
    return null;
  }
  const title = sanitizeLocalizedText(value.title, {
    required: true,
    maxLength: STOREFRONT_CONTENT_SHORT_TEXT_MAX_LENGTH,
  });
  const body = sanitizeOptionalLocalizedText(value.body, 280);
  const bodyRichText = sanitizeOptionalLocalizedRichText(value.bodyRichText, 280);
  const icon =
    value.icon === undefined || value.icon === null
      ? null
      : isOneOf(value.icon, STOREFRONT_CONTENT_ICON_TOKENS)
        ? value.icon
        : undefined;
  if (!isSafeId(value.id) || !title || !body || bodyRichText === null || icon === undefined) {
    return null;
  }
  return { id: value.id, title, body, ...(bodyRichText ? { bodyRichText } : {}), icon };
}

function sanitizeFaqItem(value: unknown): StorefrontContentFaqItemConfig | null {
  if (!isRecord(value) || !hasOnlyKeys(value, ['id', 'question', 'answer', 'answerRichText'])) {
    return null;
  }
  const question = sanitizeLocalizedText(value.question, {
    required: true,
    maxLength: 180,
  });
  const answer = sanitizeLocalizedText(value.answer, {
    required: true,
    maxLength: 700,
  });
  const answerRichText = sanitizeOptionalLocalizedRichText(value.answerRichText, 700, true);
  if (!isSafeId(value.id) || !question || !answer || answerRichText === null) return null;
  return { id: value.id, question, answer, ...(answerRichText ? { answerRichText } : {}) };
}

function sanitizeTestimonialItem(value: unknown): StorefrontContentTestimonialsItemConfig | null {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ['id', 'quote', 'quoteRichText', 'name', 'context'])
  ) {
    return null;
  }
  const quote = sanitizeLocalizedText(value.quote, {
    required: true,
    maxLength: 420,
  });
  const quoteRichText = sanitizeOptionalLocalizedRichText(value.quoteRichText, 420, true);
  const name = sanitizeLocalizedText(value.name, {
    required: true,
    maxLength: 90,
  });
  const context = sanitizeOptionalLocalizedText(value.context, 140);
  if (!isSafeId(value.id) || !quote || quoteRichText === null || !name || !context) {
    return null;
  }
  return {
    id: value.id,
    quote,
    ...(quoteRichText ? { quoteRichText } : {}),
    name,
    context,
  };
}

function sanitizeItems<TItem>(
  value: unknown,
  min: number,
  limit: number,
  sanitizeItem: (item: unknown) => TItem | null,
): readonly TItem[] | null {
  if (!Array.isArray(value) || value.length < min || value.length > limit) return null;
  const items = value.map(sanitizeItem);
  if (items.some((item) => item === null)) return null;
  const sanitized = items as TItem[];
  if (
    !hasUniqueItemIds(
      sanitized as readonly {
        readonly id: string;
      }[],
    )
  ) {
    return null;
  }
  return sanitized;
}

function sanitizeParagraphs(value: unknown): readonly StorefrontContentLocalizedText[] | null {
  if (
    !Array.isArray(value) ||
    value.length < STOREFRONT_CONTENT_ABOUT_PARAGRAPH_MIN ||
    value.length > STOREFRONT_CONTENT_ABOUT_PARAGRAPH_LIMIT
  ) {
    return null;
  }
  const paragraphs = value.map((paragraph) =>
    sanitizeLocalizedText(paragraph, {
      required: true,
      maxLength: STOREFRONT_CONTENT_BODY_TEXT_MAX_LENGTH,
    }),
  );
  return paragraphs.some((paragraph) => paragraph === null)
    ? null
    : (paragraphs as StorefrontContentLocalizedText[]);
}

function sanitizeParagraphsRichText(
  value: unknown,
  expectedCount: number,
): readonly StorefrontContentLocalizedRichText[] | undefined | null {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (!Array.isArray(value) || value.length !== expectedCount) return null;
  const paragraphs = value.map((paragraph) =>
    sanitizeStorefrontContentLocalizedRichText(paragraph, {
      required: false,
      maxLength: STOREFRONT_CONTENT_BODY_TEXT_MAX_LENGTH,
    }),
  );
  return paragraphs.some((paragraph) => paragraph === null)
    ? null
    : (paragraphs as StorefrontContentLocalizedRichText[]);
}

function sanitizeLayout<TLayout extends StorefrontContentLayoutToken>(
  value: unknown,
  allowed: readonly TLayout[],
): TLayout | null {
  return isOneOf(value, allowed) ? value : null;
}

function sanitizeCta(value: unknown): StorefrontContentCtaConfig | null {
  if (!isRecord(value) || !hasOnlyKeys(value, ['label', 'target'])) return null;
  const label = sanitizeLocalizedText(value.label, {
    required: true,
    maxLength: 70,
  });
  const target = sanitizeDestination(value.target);
  return label && target ? { label, target } : null;
}

function sanitizeTone(value: unknown): StorefrontContentSectionTone | null {
  return isOneOf(value, STOREFRONT_CONTENT_TONES) ? value : null;
}

export function sanitizeStorefrontNewsletterCollectionConfig(
  value: unknown,
): StorefrontNewsletterCollectionConfig | null {
  if (value === undefined) return structuredClone(DEFAULT_STOREFRONT_NEWSLETTER_COLLECTION_CONFIG);
  if (!isRecord(value) || !hasOnlyKeys(value, ['enabled', 'contactMethods', 'consentVersion'])) {
    return null;
  }
  if (
    typeof value.enabled !== 'boolean' ||
    value.consentVersion !== STOREFRONT_NEWSLETTER_CONSENT_VERSION ||
    !Array.isArray(value.contactMethods) ||
    value.contactMethods.length < 1 ||
    value.contactMethods.length > STOREFRONT_NEWSLETTER_CONTACT_TYPES.length
  ) {
    return null;
  }
  const contactMethods = value.contactMethods.filter(
    (method): method is StorefrontNewsletterContactType =>
      isOneOf(method, STOREFRONT_NEWSLETTER_CONTACT_TYPES),
  );
  if (contactMethods.length !== value.contactMethods.length) return null;
  if (new Set(contactMethods).size !== contactMethods.length) return null;
  return {
    enabled: value.enabled,
    contactMethods,
    consentVersion: STOREFRONT_NEWSLETTER_CONSENT_VERSION,
  };
}

export function resolveStorefrontNewsletterCollectionConfig(
  value: unknown,
): StorefrontNewsletterCollectionConfig {
  return (
    sanitizeStorefrontNewsletterCollectionConfig(value) ??
    structuredClone(DEFAULT_STOREFRONT_NEWSLETTER_COLLECTION_CONFIG)
  );
}

export function isStorefrontNewsletterCollectionEnabled(
  config: Pick<StorefrontContentSectionConfig, 'kind'> & {
    readonly collection?: StorefrontNewsletterCollectionConfig;
  },
): boolean {
  return (
    config.kind === 'newsletterCta' &&
    resolveStorefrontNewsletterCollectionConfig(config.collection).enabled
  );
}

function sanitizeBenefitsConfig(value: UnknownRecord): StorefrontContentSectionConfig | null {
  if (!hasOnlyKeys(value, ['kind', 'heading', 'body', 'bodyRichText', 'items', 'layout', 'tone'])) {
    return null;
  }
  const heading = sanitizeLocalizedText(value.heading, {
    required: true,
    maxLength: STOREFRONT_CONTENT_SHORT_TEXT_MAX_LENGTH,
  });
  const body = sanitizeOptionalLocalizedText(value.body, 360);
  const bodyRichText = sanitizeOptionalLocalizedRichText(value.bodyRichText, 360);
  const items = sanitizeItems(
    value.items,
    STOREFRONT_CONTENT_BENEFIT_ITEM_MIN,
    STOREFRONT_CONTENT_BENEFIT_ITEM_LIMIT,
    sanitizeBenefitItem,
  );
  const layout = sanitizeLayout(value.layout, ['cards', 'list']);
  const tone = sanitizeTone(value.tone);
  return heading && body && bodyRichText !== null && items && layout && tone
    ? {
        kind: 'benefits',
        heading,
        body,
        ...(bodyRichText ? { bodyRichText } : {}),
        items,
        layout,
        tone,
      }
    : null;
}

function sanitizeFaqConfig(value: UnknownRecord): StorefrontContentSectionConfig | null {
  if (!hasOnlyKeys(value, ['kind', 'heading', 'body', 'bodyRichText', 'items', 'layout', 'tone'])) {
    return null;
  }
  const heading = sanitizeLocalizedText(value.heading, {
    required: true,
    maxLength: STOREFRONT_CONTENT_SHORT_TEXT_MAX_LENGTH,
  });
  const body = sanitizeOptionalLocalizedText(value.body, 360);
  const bodyRichText = sanitizeOptionalLocalizedRichText(value.bodyRichText, 360);
  const items = sanitizeItems(
    value.items,
    STOREFRONT_CONTENT_FAQ_ITEM_MIN,
    STOREFRONT_CONTENT_FAQ_ITEM_LIMIT,
    sanitizeFaqItem,
  );
  const layout = sanitizeLayout(value.layout, ['accordion', 'list']);
  const tone = sanitizeTone(value.tone);
  return heading && body && bodyRichText !== null && items && layout && tone
    ? { kind: 'faq', heading, body, ...(bodyRichText ? { bodyRichText } : {}), items, layout, tone }
    : null;
}

function sanitizeAboutConfig(value: UnknownRecord): StorefrontContentSectionConfig | null {
  if (
    !hasOnlyKeys(value, ['kind', 'heading', 'paragraphs', 'paragraphsRichText', 'layout', 'tone'])
  ) {
    return null;
  }
  const heading = sanitizeLocalizedText(value.heading, {
    required: true,
    maxLength: STOREFRONT_CONTENT_SHORT_TEXT_MAX_LENGTH,
  });
  const paragraphs = sanitizeParagraphs(value.paragraphs);
  const paragraphsRichText = paragraphs
    ? sanitizeParagraphsRichText(value.paragraphsRichText, paragraphs.length)
    : null;
  const layout = sanitizeLayout(value.layout, ['story', 'banner']);
  const tone = sanitizeTone(value.tone);
  return heading && paragraphs && paragraphsRichText !== null && layout && tone
    ? {
        kind: 'aboutStore',
        heading,
        paragraphs,
        ...(paragraphsRichText ? { paragraphsRichText } : {}),
        layout,
        tone,
      }
    : null;
}

function sanitizeTestimonialsConfig(value: UnknownRecord): StorefrontContentSectionConfig | null {
  if (!hasOnlyKeys(value, ['kind', 'heading', 'body', 'bodyRichText', 'items', 'layout', 'tone'])) {
    return null;
  }
  const heading = sanitizeLocalizedText(value.heading, {
    required: true,
    maxLength: STOREFRONT_CONTENT_SHORT_TEXT_MAX_LENGTH,
  });
  const body = sanitizeOptionalLocalizedText(value.body, 360);
  const bodyRichText = sanitizeOptionalLocalizedRichText(value.bodyRichText, 360);
  const items = sanitizeItems(
    value.items,
    STOREFRONT_CONTENT_TESTIMONIAL_ITEM_MIN,
    STOREFRONT_CONTENT_TESTIMONIAL_ITEM_LIMIT,
    sanitizeTestimonialItem,
  );
  const layout = sanitizeLayout(value.layout, ['quotes', 'cards']);
  const tone = sanitizeTone(value.tone);
  return heading && body && bodyRichText !== null && items && layout && tone
    ? {
        kind: 'testimonials',
        heading,
        body,
        ...(bodyRichText ? { bodyRichText } : {}),
        items,
        layout,
        tone,
      }
    : null;
}

function sanitizeNewsletterConfig(value: UnknownRecord): StorefrontContentSectionConfig | null {
  if (
    !hasOnlyKeys(value, [
      'kind',
      'heading',
      'body',
      'bodyRichText',
      'button',
      'collection',
      'layout',
      'tone',
    ])
  ) {
    return null;
  }
  const heading = sanitizeLocalizedText(value.heading, {
    required: true,
    maxLength: STOREFRONT_CONTENT_SHORT_TEXT_MAX_LENGTH,
  });
  const body = sanitizeLocalizedText(value.body, {
    required: true,
    maxLength: 420,
  });
  const bodyRichText = sanitizeOptionalLocalizedRichText(value.bodyRichText, 420, true);
  const layout = sanitizeLayout(value.layout, ['banner', 'cards']);
  const tone = sanitizeTone(value.tone);
  const collection = sanitizeStorefrontNewsletterCollectionConfig(value.collection);
  if (!heading || !body || bodyRichText === null || !layout || !tone || !collection) return null;
  if (value.button === null)
    return {
      kind: 'newsletterCta',
      heading,
      body,
      ...(bodyRichText ? { bodyRichText } : {}),
      button: null,
      collection,
      layout,
      tone,
    };
  const button = sanitizeCta(value.button);
  return button
    ? {
        kind: 'newsletterCta',
        heading,
        body,
        ...(bodyRichText ? { bodyRichText } : {}),
        button,
        collection,
        layout,
        tone,
      }
    : null;
}

/**
 * Closed field-name allow-list for `content.editorialBanner`. MUST stay byte-identical (as a
 * set) to the SQL allow-list in `f086_validate_content_section_config`'s `editorialBanner`
 * branch (migration `f141_editorial_banner_section.sql`); a mismatch rejects every save at
 * runtime with no typecheck/lint/unit signal.
 */
const EDITORIAL_BANNER_CONFIG_KEYS = [
  'kind',
  'heading',
  'body',
  'bodyRichText',
  'button',
  'media',
  'layout',
  'tone',
] as const;

function sanitizeEditorialBannerConfig(
  value: UnknownRecord,
): StorefrontContentSectionConfig | null {
  if (!hasOnlyKeys(value, EDITORIAL_BANNER_CONFIG_KEYS)) return null;
  const heading = sanitizeLocalizedText(value.heading, {
    required: true,
    maxLength: STOREFRONT_CONTENT_SHORT_TEXT_MAX_LENGTH,
  });
  const body = sanitizeOptionalLocalizedText(value.body, 420);
  const bodyRichText = sanitizeOptionalLocalizedRichText(value.bodyRichText, 420, false);
  const media: StorefrontContentMediaConfig | null = sanitizeStorefrontContentMediaConfig(
    value.media,
  );
  const layout = sanitizeLayout(value.layout, ['split', 'overlay']);
  const tone = sanitizeTone(value.tone);
  if (!heading || !body || bodyRichText === null || !media || !layout || !tone) return null;
  if (value.button === null)
    return {
      kind: 'editorialBanner',
      heading,
      body,
      ...(bodyRichText ? { bodyRichText } : {}),
      button: null,
      media,
      layout,
      tone,
    };
  const button = sanitizeCta(value.button);
  return button
    ? {
        kind: 'editorialBanner',
        heading,
        body,
        ...(bodyRichText ? { bodyRichText } : {}),
        button,
        media,
        layout,
        tone,
      }
    : null;
}

export function sanitizeStorefrontContentSectionConfig(
  value: unknown,
  expectedKind?: StorefrontContentSectionKind,
): StorefrontContentSectionConfig | null {
  if (!isRecord(value) || typeof value.kind !== 'string') return null;
  if (expectedKind && value.kind !== expectedKind) return null;
  switch (value.kind) {
    case 'benefits':
      return sanitizeBenefitsConfig(value);
    case 'faq':
      return sanitizeFaqConfig(value);
    case 'aboutStore':
      return sanitizeAboutConfig(value);
    case 'testimonials':
      return sanitizeTestimonialsConfig(value);
    case 'newsletterCta':
      return sanitizeNewsletterConfig(value);
    case 'editorialBanner':
      return sanitizeEditorialBannerConfig(value);
    default:
      return null;
  }
}

export function isStorefrontContentSectionConfig(
  value: unknown,
  expectedKind?: StorefrontContentSectionKind,
): value is StorefrontContentSectionConfig {
  return sanitizeStorefrontContentSectionConfig(value, expectedKind) !== null;
}

export function validateStorefrontContentSectionConfig(
  value: unknown,
  expectedKind?: StorefrontContentSectionKind,
): {
  readonly ok: boolean;
  readonly config: StorefrontContentSectionConfig | null;
} {
  const config = sanitizeStorefrontContentSectionConfig(value, expectedKind);
  return { ok: config !== null, config };
}

export function resolveStorefrontContentText(
  value: StorefrontContentLocalizedText,
  locale: StorefrontContentLocale,
  fallbackLocale: StorefrontContentLocale = 'ar',
): string {
  const primary = value[locale]?.trim();
  if (primary) return primary;
  const fallback = value[fallbackLocale]?.trim();
  if (fallback) return fallback;
  for (const candidate of STOREFRONT_CONTENT_LOCALES) {
    const text = value[candidate]?.trim();
    if (text) return text;
  }
  return '';
}

export function resolveStorefrontContentRichText(
  value: StorefrontContentLocalizedRichText | undefined,
  fallbackText: StorefrontContentLocalizedText,
  locale: StorefrontContentLocale,
  fallbackLocale: StorefrontContentLocale = 'ar',
  maxLength: number = STOREFRONT_CONTENT_BODY_TEXT_MAX_LENGTH,
): StorefrontContentRichTextDocument | null {
  const localized = value
    ? sanitizeStorefrontContentLocalizedRichText(value, { required: false, maxLength })
    : null;
  const orderedLocales = [locale, fallbackLocale, ...STOREFRONT_CONTENT_LOCALES].filter(
    (candidate, index, all) => all.indexOf(candidate) === index,
  );
  for (const candidate of orderedLocales) {
    if (localized) {
      const document = localized[candidate];
      if (extractStorefrontContentRichTextPlainText(document)) return document;
    }
    const plain = fallbackText[candidate]?.trim();
    if (plain) return createStorefrontContentRichTextDocumentFromPlainText(plain, { maxLength });
  }
  return null;
}

export function createBlankStorefrontContentItem(
  kind: Extract<StorefrontContentSectionKind, 'benefits' | 'faq' | 'testimonials'>,
  index: number,
):
  | StorefrontContentBenefitItemConfig
  | StorefrontContentFaqItemConfig
  | StorefrontContentTestimonialsItemConfig {
  const id = `${kind.replace(/[A-Z]/g, '').toLowerCase()}-${index + 1}`;
  const empty = { ar: '', he: '', en: '' };
  if (kind === 'benefits') {
    return { id, title: empty, body: empty, icon: 'sparkles' };
  }
  if (kind === 'faq') {
    return { id, question: empty, answer: empty };
  }
  return { id, quote: empty, name: empty, context: empty };
}

export function getDefaultStorefrontContentSectionConfig(
  kind: StorefrontContentSectionKind,
): StorefrontContentSectionConfig {
  switch (kind) {
    case 'benefits':
      return {
        kind,
        heading: {
          ar: 'لماذا يختارون متجرنا',
          he: 'למה בוחרים בחנות שלנו',
          en: 'Why customers choose us',
        },
        body: {
          ar: 'اكتب وعود المتجر الأساسية بوضوح للزبائن.',
          he: 'כתבו את הבטחות החנות המרכזיות ללקוחות.',
          en: 'Describe the core promises your store makes to customers.',
        },
        layout: 'cards',
        tone: 'warm',
        items: [
          {
            id: 'benefit-1',
            icon: 'shield-check',
            title: { ar: 'جودة موثوقة', he: 'איכות אמינה', en: 'Trusted quality' },
            body: {
              ar: 'منتجات مختارة بعناية وتجربة شراء واضحة.',
              he: 'מוצרים שנבחרו בקפידה וחווית קניה ברורה.',
              en: 'Carefully selected products and a clear shopping experience.',
            },
          },
          {
            id: 'benefit-2',
            icon: 'truck',
            title: { ar: 'تواصل سريع', he: 'תקשורת מהירה', en: 'Fast communication' },
            body: {
              ar: 'يسهل على الزبائن الوصول إليكم عند الحاجة.',
              he: 'קל ללקוחות לפנות אליכם כשצריך.',
              en: 'Customers can reach you easily when they need help.',
            },
          },
        ],
      };
    case 'faq':
      return {
        kind,
        heading: { ar: 'أسئلة شائعة', he: 'שאלות נפוצות', en: 'FAQ' },
        body: {
          ar: 'أجب عن الأسئلة التي تتكرر قبل الشراء.',
          he: 'ענו על שאלות שחוזרות לפני קניה.',
          en: 'Answer common questions before checkout.',
        },
        layout: 'accordion',
        tone: 'neutral',
        items: [
          {
            id: 'faq-1',
            question: {
              ar: 'كيف يمكنني التواصل مع المتجر؟',
              he: 'איך אפשר ליצור קשר עם החנות?',
              en: 'How can I contact the store?',
            },
            answer: {
              ar: 'استخدموا أزرار التواصل المتاحة في صفحة المتجر.',
              he: 'השתמשו בכפתורי יצירת הקשר שבעמוד החנות.',
              en: 'Use the contact actions available on the store page.',
            },
          },
          {
            id: 'faq-2',
            question: {
              ar: 'ما المعلومات التي يجب أن أقرأها قبل الطلب؟',
              he: 'איזה מידע כדאי לקרוא לפני הזמנה?',
              en: 'What should I read before ordering?',
            },
            answer: {
              ar: 'راجعوا تفاصيل المنتج وخيارات التواصل الظاهرة في المتجر.',
              he: 'בדקו את פרטי המוצר ואפשרויות יצירת הקשר שמופיעות בחנות.',
              en: 'Review the product details and the contact options shown in the store.',
            },
          },
        ],
      };
    case 'aboutStore':
      return {
        kind,
        heading: { ar: 'عن المتجر', he: 'על החנות', en: 'About the store' },
        paragraphs: [
          {
            ar: 'اكتبوا قصة قصيرة عن المتجر، ما يميزه، وما الذي يجب أن يعرفه الزبائن.',
            he: 'כתבו סיפור קצר על החנות, מה מייחד אותה ומה חשוב ללקוחות לדעת.',
            en: 'Write a short story about the store, what makes it different, and what customers should know.',
          },
        ],
        layout: 'story',
        tone: 'warm',
      };
    case 'testimonials':
      return {
        kind,
        heading: { ar: 'كلمات من زبائننا', he: 'מילים מהלקוחות שלנו', en: 'Customer notes' },
        body: {
          ar: 'أضفوا اقتباسات كتبها التاجر فقط، بدون ربطها ببيانات زبائن أو طلبات.',
          he: 'הוסיפו ציטוטים שהסוחר כתב בלבד, בלי חיבור ללקוחות או להזמנות.',
          en: 'Add merchant-authored quotes only, without linking to customer or order data.',
        },
        layout: 'quotes',
        tone: 'neutral',
        items: [
          {
            id: 'testimonial-1',
            quote: {
              ar: 'اكتبوا هنا اقتباسًا قصيرًا وآمنًا للنشر.',
              he: 'כתבו כאן ציטוט קצר ובטוח לפרסום.',
              en: 'Write a short, safe-to-publish quote here.',
            },
            name: { ar: 'زبون محلي', he: 'לקוח מקומי', en: 'Local customer' },
            context: { ar: '', he: '', en: '' },
          },
        ],
      };
    case 'newsletterCta':
      return {
        kind,
        heading: { ar: 'تابعوا جديدنا', he: 'עקבו אחרי החדש אצלנו', en: 'Follow what is new' },
        body: {
          ar: 'اتركوا وسيلة تواصل لتصلكم أخبار المتجر والعروض المهمة.',
          he: 'השאירו פרטי קשר כדי לקבל עדכוני חנות ומבצעים חשובים.',
          en: 'Leave a contact method to receive store updates and important offers.',
        },
        button: {
          label: { ar: 'شاهدوا العروض', he: 'ראו מבצעים', en: 'View offers' },
          target: { type: 'store-sales' },
        },
        collection: structuredClone(DEFAULT_STOREFRONT_NEWSLETTER_COLLECTION_CONFIG),
        layout: 'banner',
        tone: 'accent',
      };
    case 'editorialBanner':
      return {
        kind,
        heading: { ar: 'قصة تستحق أن تُروى', he: 'סיפור ששווה לספר', en: 'A story worth telling' },
        body: {
          ar: 'قدّموا مجموعة أو موسمًا أو وعدًا يميّز متجركم بصورة وبضع كلمات.',
          he: 'הציגו קולקציה, עונה או הבטחה שמייחדת את החנות שלכם בתמונה ובכמה מילים.',
          en: 'Introduce a collection, a season or a promise that sets your store apart with one image and a few words.',
        },
        button: null,
        media: { desktop: null, mobile: null },
        layout: 'split',
        tone: 'neutral',
      };
  }
}
